<template>
  <Table
    :columns="columns"
    :data="rows"
    :is-row-clickable="statsClickable"
    :order="order"
    @row-click="handleRowClick"
    @sort="handleSort"
  >
    <template #rank="{ row }">
      <div
        v-if="row.rank"
        class="w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs"
        :class="getMedalClass(row.rank)"
      >
        {{ row.rank }}
      </div>
    </template>
    <template #name="{ row }">
      <div class="flex flex-col min-w-0">
        <span class="font-semibold text-gray-900 break-words md:break-normal">{{ row.name }}</span>
        <div v-if="row.ownershipType || row.region" class="mt-0.5 flex items-center gap-1.5">
          <span
            v-if="row.ownershipType"
            class="inline-flex items-center rounded-full px-1.5 py-0.5 text-[0.625rem] font-medium leading-none"
            :class="getOwnershipTypeBadgeClass(row.ownershipType)"
          >
            {{ t(`store.ownership_type.${row.ownershipType}`) }}
          </span>
          <span v-if="row.ownershipType && row.region" class="text-[0.625rem] text-gray-300">·</span>
          <span v-if="row.region" class="text-[0.625rem] text-gray-400">{{ row.region }}</span>
        </div>
        <div class="block md:hidden mt-0.5 text-xs text-gray-500 whitespace-normal">
          <span v-if="showGrossSalesCount && row.grossSalesCount != null">
            <span class="font-semibold text-gray-700">{{ t("gross_sales_inline", row.grossSalesCount) }}</span>
          </span>
          <span v-else-if="!showGrossSalesCount && row.salesCount != null">
            <span class="font-semibold text-gray-700">{{ t("roadmap.sales", row.salesCount) }}</span>
            <span v-if="row.salesCountTarget != null && row.salesCountTarget != 0" class="text-gray-400 ml-0.5">
              / {{ row.salesCountTarget }} {{ getTargetBadge(row.salesCountResultTarget) }}
            </span>
          </span>
          <span v-if="showActiveStoresRate && activeStoresRateValue(row) != null" class="ml-1 text-gray-400">
            · {{ t("active_stores_inline", { rate: formatPercentage(activeStoresRateValue(row)) }) }}
          </span>
          <span v-if="hasSeatsOrFurniture && row.complianceRate != null" class="ml-1">
            ·
            <span :class="row.complianceRate >= 0.75 ? 'text-green-600' : 'text-red-600'">
              {{ t("compliance_inline", { rate: formatPercentage(row.complianceRate) }) }}
            </span>
          </span>
          <span v-if="showTrainingCompletion && row.trainingCompletionRate != null" class="ml-1">
            ·
            <span :class="trainingRateClass(row.trainingCompletionRate)">
              {{ t("training_inline", { rate: formatTrainingRate(row.trainingCompletionRate) }) }}
            </span>
          </span>
        </div>
      </div>
    </template>
    <template v-if="showGrossSalesCount" #grossSalesCount="{ row }">
      <div class="text-right">
        <template v-if="row.grossSalesCount != null">
          <div class="font-bold">{{ t("roadmap.sales", row.grossSalesCount) }}</div>
          <div v-if="row.salesCount != null" class="text-xs text-gray-500">
            {{ t("product_sales_inline", row.salesCount) }}
          </div>
        </template>
        <span v-else class="text-gray-400">—</span>
      </div>
    </template>
    <template v-if="showGrossSalesBreakdown" #categorySplit="{ row }">
      <GrossSalesCategorySplit :breakdown="row.grossSalesBreakdown" />
    </template>
    <template v-if="showGrossSalesBreakdown" #priceBands="{ row }">
      <GrossSalesPriceBands :breakdown="row.grossSalesBreakdown" />
    </template>
    <template #salesCombined="{ row }">
      <div class="text-right">
        <span v-if="row.salesCount != null && row.salesCountTarget != null && row.salesCountTarget != 0">
          <span class="font-bold">{{ t("roadmap.sales", row.salesCount) }}</span>
          <em v-if="row.leadsProcessingCount > 0" class="hidden md:inline text-xs text-gray-500 ml-1">
            {{ t("sepa_pending", { count: row.leadsProcessingCount }) }}
          </em>
          <span class="hidden md:inline text-sm text-gray-500 ml-1">/ {{ row.salesCountTarget }}</span>
          <span class="ml-1">{{ getTargetBadge(row.salesCountResultTarget) }}</span>
        </span>
        <span v-else-if="row.salesCount != null">
          <span class="font-bold">{{ t("roadmap.sales", row.salesCount) }}</span>
          <em v-if="row.leadsProcessingCount > 0" class="hidden md:inline text-xs text-gray-500 ml-1">
            {{ t("sepa_pending", { count: row.leadsProcessingCount }) }}
          </em>
        </span>
        <span v-else class="text-gray-400">—</span>
      </div>
    </template>
    <template #complianceRate="{ row }">
      <div
        class="text-right font-thin"
        :class="
          row.complianceRate != null
            ? row.complianceRate >= 0.75
              ? 'text-green-600'
              : 'text-red-600'
            : 'text-gray-500'
        "
      >
        {{ formatPercentage(row.complianceRate) ?? "—" }}
      </div>
    </template>
    <template v-if="showTrainingCompletion" #trainingCompletionRate="{ row }">
      <div class="text-right font-thin" :class="trainingRateClass(row.trainingCompletionRate)">
        {{ formatTrainingRate(row.trainingCompletionRate) ?? "—" }}
      </div>
    </template>
    <template v-if="showActiveStoresRate" #activeStoresRate="{ row }">
      <div class="text-right font-semibold text-gray-700">
        {{ formatPercentage(activeStoresRateValue(row)) ?? "—" }}
      </div>
    </template>
    <template #cancellationRate="{ row }">
      <div class="text-right font-thin text-gray-500">
        {{ formatPercentage(row.cancellationRate) ?? "—" }}
      </div>
    </template>
    <template #projectionCombined="{ row }">
      <div class="text-right">
        <template v-if="projectionValue(row) != null">
          <div class="font-bold">{{ t("roadmap.sales", projectionValue(row)) }}</div>
          <div class="text-xs" :class="getVariationColorClass(projectionVariation(row))">
            <template v-if="projectionVariation(row) != null">
              {{ getVariationArrow(projectionVariation(row)) }}
              {{ formatVariationWithSign(projectionVariation(row)) }} %
            </template>
            <template v-else>—</template>
          </div>
        </template>
        <span v-else class="text-gray-400">—</span>
      </div>
    </template>
  </Table>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Table, formatPercentage } from "@estaly/ui";
import { formatVariation } from "@/utils/variation_formatter";
import { format, subMonths, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { mapGetters } from "vuex";
import { LEVEL_CLASSES, completionLevel } from "@/utils/trainingCompletion";
import GrossSalesCategorySplit from "@/components/analytics/retail/breakdown/GrossSalesCategorySplit.vue";
import GrossSalesPriceBands from "@/components/analytics/retail/breakdown/GrossSalesPriceBands.vue";

export default {
  components: {
    Table,
    GrossSalesCategorySplit,
    GrossSalesPriceBands,
  },

  emits: ["sort", "row-clicked"],

  setup() {
    const { t, te } = useI18n();
    return { t, te };
  },

  props: {
    stats: {
      type: Array,
      required: true,
    },
    total: {
      type: Object,
      required: true,
    },
    currentMonth: {
      type: String,
      required: true,
    },
    statsClickable: {
      type: Boolean,
      default: false,
    },
    accessibleStoreIds: {
      type: Array,
      default: () => [],
    },
    showGrossSalesCount: {
      type: Boolean,
      default: false,
    },
    showActiveStoresRate: {
      type: Boolean,
      default: false,
    },
    showTrainingCompletion: {
      type: Boolean,
      default: false,
    },
    order: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapGetters("store", ["getOwnershipTypeBadgeClass"]),
    ...mapGetters("businessVerticals", ["hasSeatsOrFurniture"]),

    columns() {
      if (this.showGrossSalesCount) {
        return [
          { label: null, field: "rank", slot: "rank", widthClass: "w-8", headerGroup: "" },
          { field: "name", label: null, slot: "name", headerGroup: "" },
          {
            field: "gross_sales_count",
            label: this.t("gross_sales_column"),
            tooltip: this.t("gross_sales_tooltip"),
            slot: "grossSalesCount",
            headerGroup: "",
            numeric: true,
            sortable: true,
            widthClass: "hidden md:table-cell",
          },
          ...this.grossSalesBreakdownColumns,
          ...this.activeStoresColumns,
          ...(this.hasSeatsOrFurniture
            ? [
                {
                  field: "compliance_rate",
                  label: this.t("compliance_column"),
                  slot: "complianceRate",
                  headerGroup: "",
                  numeric: true,
                  sortable: true,
                  widthClass: "hidden md:table-cell",
                },
              ]
            : []),
          ...this.trainingColumns,
          {
            field: "cancellation_rate",
            label: this.t("cancellation_column"),
            slot: "cancellationRate",
            headerGroup: "",
            numeric: true,
            sortable: true,
            widthClass: "hidden md:table-cell",
          },
          {
            field: "gross_sales_count_projection",
            label: this.t("projection_column", { month: this.formattedShortMonth }),
            tooltip: this.t("projection_tooltip", { month: this.formattedPreviousMonth }),
            slot: "projectionCombined",
            headerGroup: "",
            numeric: true,
            sortable: true,
            widthClass: "hidden md:table-cell",
          },
        ];
      }

      return [
        { label: null, field: "rank", slot: "rank", widthClass: "w-8", headerGroup: "" },
        { field: "name", label: null, slot: "name", headerGroup: "" },
        {
          field: "sales_count",
          label: this.hasSalesTarget ? this.t("sales_target_column") : this.t("sales_column"),
          tooltip: this.hasSalesTarget ? this.t("sales_target_tooltip") : null,
          slot: "salesCombined",
          headerGroup: "",
          numeric: true,
          sortable: true,
          widthClass: "hidden md:table-cell",
        },
        ...this.activeStoresColumns,
        ...(this.hasSeatsOrFurniture
          ? [
              {
                field: "compliance_rate",
                label: this.t("compliance_column"),
                slot: "complianceRate",
                headerGroup: "",
                numeric: true,
                sortable: true,
                widthClass: "hidden md:table-cell",
              },
            ]
          : []),
        ...this.trainingColumns,
        {
          field: "cancellation_rate",
          label: this.t("cancellation_column"),
          slot: "cancellationRate",
          headerGroup: "",
          numeric: true,
          sortable: true,
          widthClass: "hidden md:table-cell",
        },
        {
          field: "sales_count_projection",
          label: this.t("projection_column", { month: this.formattedShortMonth }),
          tooltip: this.t("projection_tooltip", { month: this.formattedPreviousMonth }),
          slot: "projectionCombined",
          headerGroup: "",
          numeric: true,
          sortable: true,
          widthClass: "hidden md:table-cell",
        },
      ];
    },

    // Share of stores with at least one sale, counted on gross sales or on sales like the main column
    activeStoresColumns() {
      if (!this.showActiveStoresRate) return [];

      return [
        {
          field: this.showGrossSalesCount ? "gross_sales_active_stores_rate" : "active_stores_rate",
          label: this.t("active_stores_column"),
          tooltip: this.t("active_stores_tooltip"),
          slot: "activeStoresRate",
          headerGroup: "",
          numeric: true,
          sortable: true,
          widthClass: "hidden md:table-cell",
        },
      ];
    },

    // Seats / furniture split and price bands, only when the shown stores sold some
    showGrossSalesBreakdown() {
      return this.showGrossSalesCount && this.hasSeatsOrFurniture && this.total?.grossSalesBreakdown?.total > 0;
    },

    // Offers in the API order, the one of the percentages below each bar (e.g. "Meuble / Siège")
    categorySplitLabel() {
      return (this.total?.grossSalesBreakdown?.categories || [])
        .map(({ category }) => {
          const shortLabelKey = `offer_short_label.${category}`;
          return this.te(shortLabelKey) ? this.t(shortLabelKey) : this.t(`contract_distributions.category.${category}`);
        })
        .join(" / ");
    },

    grossSalesBreakdownColumns() {
      if (!this.showGrossSalesBreakdown) return [];
      return [
        {
          field: "category_split",
          label: this.categorySplitLabel,
          tooltip: this.t("category_split_tooltip"),
          slot: "categorySplit",
          headerGroup: "",
          widthClass: "hidden md:table-cell w-28",
        },
        {
          field: "price_bands",
          label: this.t("price_bands_column"),
          tooltip: this.t("price_bands_tooltip"),
          slot: "priceBands",
          headerGroup: "",
          widthClass: "hidden md:table-cell w-36",
        },
      ];
    },

    trainingColumns() {
      if (!this.showTrainingCompletion) return [];
      return [
        {
          field: "training_completion_rate",
          label: this.t("training_column"),
          tooltip: this.t("training_tooltip"),
          slot: "trainingCompletionRate",
          headerGroup: "",
          numeric: true,
          sortable: true,
          widthClass: "hidden md:table-cell",
        },
      ];
    },

    // The target is only shown on rows having one (stores, regions, totals); sellers have none
    hasSalesTarget() {
      return [this.total, ...this.statsRows].some((row) => row?.salesCountTarget);
    },

    statsRows() {
      return this.stats || [];
    },

    totalRow() {
      return [this.total];
    },

    hasInaccessibleStores() {
      if (!this.statsClickable || !this.accessibleStoreIds.length) return false;
      return this.statsRows.some((row) => !this.accessibleStoreIds.includes(row.id));
    },

    rows() {
      const statsWithRank = this.statsRows.map((row) => {
        const isAccessible = !this.hasInaccessibleStores || this.accessibleStoreIds.includes(row.id);

        return {
          ...row,
          clickable: this.statsClickable && isAccessible,
          body: true,
          rowClass: this.hasInaccessibleStores && isAccessible ? "bg-primary-light/20" : "",
        };
      });

      return [
        ...this.totalRow.map((row) => ({
          ...row,
          rank: null,
          clickable: false,
          rowClass: "bg-gray-100",
        })),
        ...statsWithRank,
      ];
    },

    formattedShortMonth() {
      if (!this.currentMonth) return "";
      return this.formatShortMonth(this.currentMonth);
    },

    formattedPreviousMonth() {
      if (!this.currentMonth) return "M-1";
      return this.formatShortMonth(this.currentMonth, true);
    },
  },

  methods: {
    // Training rate is a 0-100 integer, with the same colours as the stores training follow-up
    formatTrainingRate(rate) {
      return rate == null ? null : formatPercentage(rate / 100);
    },

    trainingRateClass(rate) {
      return LEVEL_CLASSES[completionLevel(rate)].text;
    },

    handleRowClick(row) {
      if (!row.clickable) return;
      this.$emit("row-clicked", row);
    },

    handleSort(column) {
      this.$emit("sort", column);
    },

    projectionValue(row) {
      return this.showGrossSalesCount ? row.grossSalesCountProjection : row.salesCountProjection;
    },

    activeStoresRateValue(row) {
      return this.showGrossSalesCount ? row.grossSalesActiveStoresRate : row.activeStoresRate;
    },

    projectionVariation(row) {
      return this.showGrossSalesCount ? row.grossSalesCountProjectionVariation : row.salesCountProjectionVariation;
    },

    getMedalClass(ranking) {
      if (ranking === 0 || ranking == null) return "";

      const classes = [
        "bg-linear-to-r/srgb from-yellow-400 to-yellow-500 text-white",
        "bg-linear-to-r/srgb from-gray-300 to-gray-400 text-white",
        "bg-linear-to-r/srgb from-orange-400 to-orange-800 text-white",
      ];
      return classes[ranking - 1] || "bg-gray-100 text-gray-600";
    },

    formatVariation,
    formatPercentage,

    formatShortMonth(monthString, previous = false) {
      if (!monthString) return previous ? "M-1" : "";

      try {
        const date = parse(monthString, "MMMM yyyy", new Date(), { locale: fr });
        if (isNaN(date.getTime())) return previous ? "M-1" : monthString;

        const targetDate = previous ? subMonths(date, 1) : date;
        return format(targetDate, "MMM yy", { locale: fr });
      } catch (error) {
        return previous ? "M-1" : monthString;
      }
    },

    getVariationArrow(variation) {
      if (variation == null) return "";
      const formatted = formatVariation(variation);
      if (formatted > 0 || formatted === "+ ∞") {
        return "▲";
      }
      if (formatted < 0) {
        return "▼";
      }
      return "";
    },

    getVariationColorClass(variation) {
      if (variation == null) return "text-gray-500";
      const formatted = formatVariation(variation);
      if (formatted > 0 || formatted === "+ ∞") {
        return "text-green-600";
      }
      if (formatted < 0) {
        return "text-red-600";
      }
      return "text-gray-500";
    },

    formatVariationWithSign(variation) {
      if (variation == null) return formatVariation(variation);
      const formatted = formatVariation(variation);
      if (formatted > 0 && typeof formatted === "number") {
        return `+${formatted}`;
      }
      return formatted;
    },

    getTargetBadge(percentage) {
      if (percentage == null) return "";
      if (percentage >= 80) {
        return "🟢";
      }
      if (percentage >= 60) {
        return "🟠";
      }
      return "🔴";
    },
  },
};
</script>

<i18n src="./CommonAnalyticsTable.i18n.json"></i18n>
